import is from '@sindresorhus/is';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useSnackbars } from '../../../app/error-handling/SnackBars';
import type { Comment, CommentTarget } from '../../../data-contract/comment';
import { useApplicationFetchers } from '../../../fetchers/use-application-fetchers';
import { useCurrentCommentAuthor } from './use-current-comment-author';
import { repliesQueryKey } from './use-replies-query';

type UseCreateReplyParams = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly language: string;
    readonly parentCommentId: string;
    readonly version: number;
};

export type CreateReplyVariables = {
    readonly content: string;
    readonly id: string;
    readonly parentTarget: CommentTarget;
};

type CreateReplyContext = {
    readonly previousReplies: readonly Comment[] | undefined;
};

export const useCreateReply = ({
    architectureId,
    itemId,
    language,
    parentCommentId,
    version,
}: UseCreateReplyParams): UseMutationResult<Comment, Error, CreateReplyVariables, CreateReplyContext> => {
    const { createReply } = useApplicationFetchers();
    const queryClient = useQueryClient();
    const author = useCurrentCommentAuthor();
    const { showErrorSnackbar } = useSnackbars();

    const repliesKey = repliesQueryKey({ architectureId, itemId, parentCommentId, version });

    return useMutation<Comment, Error, CreateReplyVariables, CreateReplyContext>({
        mutationKey: ['create-reply', architectureId, itemId, version, parentCommentId],
        mutationFn: async (variables) => {
            const result = await createReply({
                architectureId,
                body: { content: variables.content, id: variables.id },
                commentId: parentCommentId,
                itemId,
            });
            if (result.isErr) {
                throw result.error;
            }
            return result.value;
        },
        onError: (error, _variables, context) => {
            if (is.undefined(context)) return;
            queryClient.setQueryData<readonly Comment[]>(repliesKey, context.previousReplies);
            showErrorSnackbar(error);
        },
        onMutate: async (variables) => {
            await queryClient.cancelQueries({ queryKey: repliesKey });

            const previousReplies = queryClient.getQueryData<readonly Comment[]>(repliesKey);

            const optimisticReply: Comment = {
                author,
                content: variables.content,
                createdAt: new Date().toISOString(),
                id: variables.id,
                parentId: parentCommentId,
                sourceLanguage: language,
                target: variables.parentTarget,
                version,
            };

            queryClient.setQueryData<readonly Comment[]>(repliesKey, (prev) =>
                is.undefined(prev) ? [optimisticReply] : [...prev, optimisticReply],
            );

            return { previousReplies };
        },
        onSuccess: (data, variables) => {
            queryClient.setQueryData<readonly Comment[]>(repliesKey, (prev) => {
                if (is.undefined(prev)) return [data];
                const idx = prev.findIndex((reply) => reply.id === variables.id);
                if (idx < 0) return [...prev, data];
                const next = [...prev];
                next[idx] = data;
                return next;
            });
            queryClient.invalidateQueries({ queryKey: repliesKey });
        },
        throwOnError: false,
    });
};
